import seedrandom from 'seedrandom';

import type { Element, ElementContext, Field, Item, Step, StepInfo, Survey } from '@/types/survey';

/**
 * When a step's `isRandomized` property is true, randomize the order of its items using a specified random seed.
 *
 * @param step - The survey step whose item order may be randomized.
 * @param randomSeed - The respondent's integer seed used to randomize the item order.
 * @returns A new Step object identical to the original, but with a randomized item order if applicable.
 */
function applyRandomSeedToStep(step: Step, randomSeed: number): Step {
  // If randomization is not enabled, return the step unchanged.
  if (!step.isRandomized) return step;

  const randomNumberGenerator = seedrandom(String(randomSeed));
  const shuffledItems: Item[] = [...step.items].sort(() => randomNumberGenerator() - 0.5);
  return { ...step, items: shuffledItems };
}

/**
 * For a given survey, randomize the order of items within all steps that have the `isRandomized` property set to true.
 *
 * @param randomSeed - The respondent's random seed used to shuffle item order within steps.
 * @param survey - The survey to randomize.
 * @returns A new Survey object identical to the original but with modified Step objects.
 */
export function applyRandomSeedToSurvey(randomSeed: number | undefined, survey: Survey): Survey {
  // Skip if no random seed or if there are no steps to process
  if (!randomSeed || !survey?.steps?.length) return survey;

  const stepsAfterItemsShuffled: Step[] = survey.steps.map((step: Step) =>
    applyRandomSeedToStep(step, randomSeed)
  );
  return { ...survey, steps: stepsAfterItemsShuffled };
}

/**
 * Assigns page boundaries for all elements in the given range [lo...hi].
 * No-ops if the range is empty or out of order.
 */
function closePage(
  contexts: ElementContext[],
  lo: number,
  hi: number
): void {
  if (lo > hi) return;
  const min = Math.max(0, lo);
  const max = Math.min(hi, contexts.length - 1);
  for (let k = min; k <= max; k++) {
    contexts[k].pageStart = min;
    contexts[k].pageEnd = max;
  }
}

/**
 * Assigns step boundaries for all elements in the given range [lo...hi].
 *
 * @param contexts - The array of element navigation contexts.
 * @param lo - Index of the first element in the step segment.
 * @param hi - Index of the last element in the step segment.
 * @param stepId - The ID of the step these elements belong to.
 */
function closeStep(
  contexts: ElementContext[],
  lo: number,
  hi: number,
  stepId: string
): void {
  if (lo > hi) return;
  const min = Math.max(0, lo);
  const max = Math.min(hi, contexts.length - 1);
  for (let k = min; k <= max; k++) {
    contexts[k].stepStart = min;
    contexts[k].stepEnd = max;
    contexts[k].step = stepId;
  }
}

/**
 * Flattens a survey's steps into a single list of elements in render order.
 *
 * @param steps - The ordered list of survey steps.
 * @returns A flat array of elements (instructions, items, fields) in administration order.
 */
export function createFlatMapOfElements(steps: Step[]): Element[] {
  return steps.flatMap(s => [
    ...(s.instructions ?? []),
    ...(s.items ?? []),
    ...(s.fields ?? []),
  ]);
}

/**
 * Creates a flat list of step IDs aligned 1:1 with the survey's flat element list.
 *
 * Each element position in the returned array corresponds to the step that element belongs to.
 *
 * @param steps - The ordered list of survey steps.
 * @returns A flat array of step IDs aligned with the flattened element list.
 */
export function createFlatMapOfStepIds(steps: Step[]): string[] {
  return steps.flatMap(s =>
    Array((
      (s.instructions?.length ?? 0) +
      (s.items?.length ?? 0) +
      (s.fields?.length ?? 0)
    )).fill(s.id)
  )
}

/**
 * Build a map of element IDs → context metadata for each element in a survey.
 *
 * Contexts include:
 *   - element ID, index, and step ID
 *   - pageStart/pageEnd based on `isAfterPageBreak`
 *   - stepStart/stepEnd based on `stepIds`
 *
 * @param elements - Flat list of survey elements in render order.
 * @param stepIds - Flat list of step IDs aligned 1:1 with the elements.
 * @returns Map keyed by element ID with its ElementContext as the value.
 */
export function createElementContextMap(
  elements: Element[],
  stepIds: string[]
): Map<string, ElementContext> {
  const elementCount: number = elements.length;

  // Preallocate contexts aligned to indices of `elements`
  const contexts: ElementContext[] = Array.from({ length: elementCount }, () => ({
    id: '',
    index: 0,
    step: '',
    pageStart: 0,
    pageEnd: 0,
    stepStart: 0,
    stepEnd: 0,
  }));

  // Running segment starts
  let pageStart = 0;
  let stepStart = 0;

  for (let i = 0; i < elementCount; i++) {
    const el = elements[i];
    contexts[i].id = el.id;
    contexts[i].index = i;

    const stepChanged: boolean = i > 0 && stepIds[i] !== stepIds[i - 1];

    if (stepChanged) {
      // STEP BOUNDARY: close the *current page first*, then the step.
      closePage(contexts, pageStart, i - 1);
      closeStep(contexts, stepStart, i - 1, stepIds[i - 1]);

      // New step starts a new page as well
      stepStart = i;
      pageStart = i;
    } else if (el.isAfterPageBreak && i > 0) {
      // PAGE BOUNDARY within the *same* step
      closePage(contexts, pageStart, i - 1);
      pageStart = i;
    }
  }

  // Close trailing segments for the final page/step
  if (elementCount > 0) {
    closePage(contexts, pageStart, elementCount - 1);
    closeStep(contexts, stepStart, elementCount - 1, stepIds[elementCount - 1]);
  }

  return new Map<string, ElementContext>(contexts.map(c => [c.id, c]));
}

/**
 * Build a map of step IDs → step information for each step in a survey.
 *
 * Info includes a lightweight Step object minus properties of items, instructions, fields, and isRandomized.
 *
 * @param steps - List of steps in the survey.
 * @returns Map keyed by step ID with its StepInfo as the value.
 */
export function createStepMap(steps: Step[]): Map<string, StepInfo> {
  return new Map(
    steps.map(({ items, instructions, fields, isRandomized, ...rest }: Step): [string, StepInfo] => [
      rest.id,
      {
        ...rest,
        instructionCount: instructions.length,
        answerableElementIds: new Set([...items, ...fields].map((element: Item | Field) => element.id))
      }
    ])
  );
}

/**
 * For a given step, remove any fields related to request a login code be sent via email.
 *
 * @param step - The survey step from which fields are being filtered.
 * @returns A new Step object identical to the original, but with login code email request fields removed.
 */
function removeLoginCodeEmailRequestFieldsFromStep(step: Step): Step {
  const filteredFields: Field[] = step.fields.filter((field: Field) => field.category !== 'LER');
  return { ...step, fields: filteredFields };
}

/**
 * For a given survey, remove any fields related to requesting a login code be sent via email.
 *
 * @param survey - The survey from which to remove login code email request fields.
 * @returns A new Survey object identical to the original, but with updated Step objects.
 */
export function removeLoginCodeEmailRequestFieldsFromSurvey(survey: Survey): Survey {
  const modifiedSteps: Step[] = survey.steps.map(removeLoginCodeEmailRequestFieldsFromStep);
  return { ...survey, steps: modifiedSteps };
}